
import routes from '@/constants/routes';
import { isFirefox } from '@/utils/platform';
import { HttpStatusCode } from '@/constants/Http';
import { AUTHENTICATE, getAction } from '@/store/user/actions';
import api from '@/utils/api';
import gmapsHelper from '@/utils/GmapsHelper';
import { track } from '@/utils/analytics';
import { ExternalLinks } from '@/utils/external-navigation/constants';
import MetaHead from '@/mixins/MetaHead';
import proPricingDisplay from '@/components/proMarketplace/proPricingDisplay';
import { ActiveCategories } from '@/components/page-specific/pro-search/enums';

export default {
  components: {
    proPricingDisplay,
  },
  mixins: [MetaHead],
  data() {
    return {
      loading: true,
      createServiceRequestLoading: false,
      results: [],
      searching: false,
      filters: {
        lat: null,
        lon: null,
        listingId: null,
        serviceCategory: null,
        serviceType: null,
        pagination: {
          rowsPerPage: 6,
          page: 1,
        },
      },
      totalMatches: null,
      proModalOpen: false,
      proProfileLoading: false,
      proProfile: null,

      autocompleteAddress: null,
      unit: null,
      address: null,
      city: null,
      province: null,
      postalCode: null,

      serviceTreeVisible: false,
      serviceSearch: null,
      serviceSearchEntries: [],
      services: [],
      serviceCategories: [],
      serviceTypes: [],
      google: null,
      showRequestService: false,
      currentActiveQueries: {},
    };
  },
  async fetch() {
    await this.getContent();
  },
  computed: {
    user() {
      return this.$store.state.user.userData;
    },
    searchFilterDesktopClasses() {
      return {
        firefox: isFirefox(),
      };
    },
  },
  watch: {

    filters: {
      handler: 'doSearch',
      deep: true,
    },

    serviceSearch(val) {
      if (val) {
        const pair = val.split('|');
        // Update url with the new category and service type
        this.updateUrl('categoryId', parseInt(pair[0], 10));
        this.updateUrl('serviceId', parseInt(pair[1], 10));

        this.filters.serviceCategory = parseInt(pair[0], 10);
        this.refreshServiceTypes();
        this.filters.serviceType = parseInt(pair[1], 10);
        this.trackSelectCategoryServiceTypeEvent();
      }
    },
  },
  async mounted() {
    this.currentActiveQueries = this.$route.query;
    this.loading = true;

    try {
      await this.fetchServices();
      this.parseServiceType();
    } catch ({ response }) {
      if (response.status === HttpStatusCode.MovedPermanently) {
        this.forcePageRefresh();
      }
    } finally {
      this.loading = false;
    }

    await this.initLocation();
  },

  methods: {

    async fetchServices() {
      this.serviceCategories = [];
      this.serviceSearchEntries = [];
      this.services = await api.get(
        '/jobs/services',
        {
          lat: this.filters.lat,
          lon: this.filters.lon,
          category: !this.isInt(this.currentActiveQueries.categoryId)
            ? this.currentActiveQueries.categoryId
            : null,
        },
      );

      /**
       * In case of the selected address doesn't have categories and services
       * so we have to remove the categoryId and serviceI from the url
       */
      if (this.services.length === 0
        && Object.prototype.hasOwnProperty.call(this.currentActiveQueries, 'categoryId')
        && Object.prototype.hasOwnProperty.call(this.currentActiveQueries, 'serviceId')) {
        delete this.currentActiveQueries.categoryId;
        delete this.currentActiveQueries.serviceId;
        this.updateUrl(null, null);
      }
      this.services.forEach((service) => {
        this.serviceCategories.push({
          id: service.id,
          name: service.name,
        });

        service.children.forEach((type) => {
          this.serviceSearchEntries.push({
            id: `${service.id}|${type.id}`,
            text: `${service.name} / ${type.name}`,
          });
        });
      });
    },

    async getContent() {
      const { data } = await this.$prismic.client.getSingle('bodiverselp');

      if (data) {
        this.meta.title = data.meta_title;
        this.meta.description = data.meta_description;
        this.meta.image = data.meta_image;
      }
    },

    async initLocation() {
      this.google = await gmapsHelper.init();

      const location = await this.parseLocation();
      if (location) {
        this.filters.lat = location.lat();
        this.filters.lon = location.lng();
      }

      await this.loadPlaces();
    },
    /**
     * @track Track on user select category event trigger
     * @param {number} selectedCategoryId
     *
     * @private
     */
    onChangeCategory(selectedCategoryId) {
      const category = this.serviceCategories.find(
        (item) => item.id === selectedCategoryId,
      );
      track('Pro Search_Category selected', { category: category.name });
      // Update url with selected service type
      this.updateUrl('categoryId', category.name);
    },

    /**
     * @track Track on user select service type event trigger
     * @param {number} selectedEventId
     *
     * @private
     */
    onChangeServiceType(selectedEventId) {
      const serviceType = this.serviceTypes.find(
        (item) => item.id === selectedEventId,
      );
      track('Pro Search_Service type selected', {
        serviceType: serviceType.name,
      });
      // Update url with selected service type
      this.updateUrl('serviceId', serviceType.name);
    },

    navigateToProProfile(id, company, serviceName) {
      // @track Profile button click
      track('Pro Search_Prō profile button click', {
        proName: company,
      });
      // Some company names have a / in their name this fixes URL query parameter
      const url = `/pro/${id}?serviceName=${serviceName}`;
      window.open(url, '_blank');
    },

    /**
     * @track Track on user select category/service type event trigger
     *
     * @private
     */
    trackSelectCategoryServiceTypeEvent() {
      const category = this.serviceCategories.find(
        (item) => item.id === this.filters.serviceCategory,
      );
      const serviceType = this.serviceTypes.find(
        (item) => item.id === this.filters.serviceType,
      );
      track('Pro Search_Category / service type selected', {
        category: category.name,
        serviceType: serviceType.name,
      });
    },

    async loadProProfile(proId, proBusinessName) {
      // @track Event trigger
      track('Pro Search_Prō profile button click', {
        proName: proBusinessName,
      });
      this.proProfileLoading = true;
      this.proProfile = await api.get(`/pro/${proId}`, null, {
        unauthorized: true,
      });

      this.proProfileLoading = false;
      this.proModalOpen = true;
    },

    async loadPlaces() {
      const google = await gmapsHelper.init();
      // Create the autocomplete object, restricting the search predictions to
      // geographical location types.
      this.autocomplete = new google.maps.places.Autocomplete(
        document.getElementById('autocompleteAddress'),
        { types: ['geocode'] },
      );
      // Avoid paying for data that you don't need by restricting the set of
      // place fields that are returned to just the address components.
      this.autocomplete.setFields([
        'address_component',
        'geometry',
        'name',
        'formatted_address',
      ]);

      // When the user selects an address from the drop-down, populate the
      // address fields in the form.
      this.autocomplete.addListener('place_changed', async () => {
        const place = this.autocomplete.getPlace();
        // @track Change address event trigger
        track('Pro Search_Location entered', {
          location: place.formatted_address,
        });

        /**
         * - Appened address to current ur
         * - Remove previous selected
         *   category and service type url values
         */
        // this.updateUrl('address', place.formatted_address);

        if (place.geometry && place.geometry.location) {
          this.filters.lat = place.geometry.location.lat();
          this.filters.lon = place.geometry.location.lng();
          await this.fetchServices();
          this.doSearch();
        }
        let unit = '';
        let address = '';
        let city = '';
        let province = '';
        let postalCode = '';

        place.address_components.forEach((comp) => {
          if (comp.types.includes('street_number')) unit = comp.long_name;
          if (comp.types.includes('route')) address = comp.long_name;
          if (comp.types.includes('locality')) city = comp.long_name;
          if (comp.types.includes('administrative_area_level_1')) province = comp.short_name;
          if (comp.types.includes('postal_code')) postalCode = comp.short_name;
        });

        const unitAndAddress = (
          `${this.parseNull(unit)
          } ${
            this.parseNull(address)}`
        ).trim();

        const components = [];
        if (unitAndAddress && unitAndAddress.length > 0) components.push(unitAndAddress);
        if (city && city.length > 0) components.push(city);
        if (province && province.length > 0) components.push(province);

        this.autocompleteAddress = components.join(', ');
        this.unit = unit;
        this.address = address;
        this.city = city;
        this.province = province;
        this.postalCode = postalCode;
      });
    },

    parseNull(val) {
      return val == null ? '' : val;
    },

    refreshServiceTypes() {
      const FIRST_SERVICE_CATEGORY = 0;
      this.serviceTypes = [];
      let serviceTypeChanged = true;
      const service = this.services.find(({ id }) => this.filters.serviceCategory === id);
      if (service && this.filters.serviceCategory) {
        service.children.forEach((type) => {
          this.serviceTypes.push({
            id: type.id,
            name: type.name,
          });
          if (type.id === this.filters.serviceType) serviceTypeChanged = false;
        });
      }

      if (service && serviceTypeChanged) {
        this.filters.serviceType = service.children[FIRST_SERVICE_CATEGORY].id;
        this.updateUrl('serviceId', service.children[FIRST_SERVICE_CATEGORY].name);
      }
    },

    async doSearch() {
      this.refreshServiceTypes();
      if (this.searching || this.loading) return;
      this.searching = true;

      try {
        this.searching = true;
        const query = this.makeQuery(this.filters);
        const queryString = this.toQueryString(query);
        const res = await api.get(`/jobs/search?${queryString}`, null, {
          unauthorized: true,
        });
        this.totalMatches = res.fullCount;
        const {
          category, lat, lon,
        } = query;

        // Show the request service button in case when someone from dropdown will be selected
        const isShowRequestService = [
          category.length, lat, lon,
        ].some((filter) => filter);

        this.showRequestService = isShowRequestService;
        this.results = res.results;
      } finally {
        this.searching = false;
      }
    },

    makeQuery(filters) {
      return {
        lat: filters.lat ? filters.lat : '',
        lon: filters.lon ? filters.lon : '',
        service: filters.serviceType ? [filters.serviceType] : [],
        category: filters.serviceCategory ? [filters.serviceCategory] : [],
        limit: filters.pagination.rowsPerPage,
        offset: filters.pagination.rowsPerPage * (filters.pagination.page - 1),
      };
    },

    toQueryString(query) {
      return Object.keys(query)
        .map((key) => `${key}=${query[key]}`)
        .join('&');
    },

    verifyIdentity() {
      localStorage.setItem('continuePath', this.$router.currentRoute.path);
      this.$router.push('/settings/verification');
    },

    async createSingleProServiceRequest({ userId: proId, proBusinessName, categoryName }) {
      track('Pro Search_Request service button click', {
        proName: proBusinessName,
      });

      if (categoryName === ActiveCategories.Mortgages) {
        this.openMortgageVelocity();
      } else {
        await this.createServiceRequest(proId);
      }
    },

    async createServiceRequest(proId) {
      await this.$store.dispatch(getAction(AUTHENTICATE));
      this.$router.push(
        {
          path: routes.proMarketplaceServiceRequest,
          query: { for: proId, service: this.filters.serviceType },
        },
      );
    },

    openMortgageVelocity() {
      window.open(ExternalLinks.TrilogyApp);
    },

    async parseLocation() {
      const location = this.$route.query.address;
      if (location) {
        return new Promise((resolve, reject) => {
          this.autocompleteAddress = location;
          const geocoder = new this.google.maps.Geocoder();
          geocoder.geocode(
            {
              address: this.autocompleteAddress,
            },
            async (results, status) => {
              if (status !== 'OK' || !results[0]) {
                reject(status);
              } else {
                resolve(results[0].geometry.location);
              }
            },
          );
        });
      }
      return null;
    },

    isInt(value) {
      const er = /^-?[0-9]+$/;
      return er.test(value);
    },

    async parseServiceType() {
      const { categoryId, serviceId } = this.$route.query;

      if (categoryId) {
        let serviceCategory = '';
        if (this.isInt(categoryId)) {
          serviceCategory = this.serviceCategories.find((s) => s.id === parseInt(categoryId, 10));
          this.updateUrl('categoryId', serviceCategory.name);
          serviceCategory = serviceCategory.id;
        } else {
          serviceCategory = this.serviceCategories
            .find((s) => s.name.toLowerCase() === categoryId.toLowerCase()).id;
        }
        if (serviceId) {
          let serviceNo = '';
          if (this.isInt(categoryId)) {
            serviceNo = this.services.find((s) => s.id === categoryId).children
              .find((t) => t.id === serviceId);
            this.updateUrl('serviceId', serviceNo.name);
            serviceNo = serviceNo.id;
          } else {
            serviceNo = this.services
              .find((s) => s.name.toLowerCase() === categoryId.toLowerCase())
              .children
              .find((t) => t.name.toLowerCase() === serviceId.toLowerCase())
              .id;
          }
          this.filters = {
            ...this.filters,
            serviceCategory,
            serviceType: serviceNo,
          };
        } else {
          this.filters = {
            ...this.filters,
            serviceCategory,
          };
        }
      } else {
        // no category/service set, manually run search
        await this.doSearch();
      }
    },

    /**
     * Add a parameter to the url based on the arguments passed in.
     *
     * @param {string} name - The name of the parameter
     * @param {string} value - The value of the parameter
     * @returns {void}
     * @public
     */
    updateUrl(name, value) {
      // Add the new query to the saved queries
      const query = {
        ...this.currentActiveQueries,
        [name]: value.toString(),
      };
      // Save current query params
      this.currentActiveQueries = query;
      // Generate the new url with queries
      const search = Object
        .keys(query)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
        .join('&');
      // change the url with the new url
      window.history.pushState({}, null, `${this.$route.path}?${search}`);
    },

    async forcePageRefresh() {
      this.currentActiveQueries = {};
      this.$router.replace(routes.proSearch);
      await this.fetchServices();
    },
  },
};
